import { useEffect } from 'react'

import { useParams } from 'react-router'

import { hasActionPermission } from '@tribeplatform/gql-client/lib'
import { ExportStatus, Image } from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { useAnalytics } from '@tribeplatform/react-sdk'
import {
  useExportMemberRequest,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useLogin } from '../../../hooks/useLogin.js'

export const DownloadMembers = () => {
  const analytics = useAnalytics()
  const { id } = useParams<{ id: string }>()
  const { isLoggedIn, showLogin } = useLogin()
  const { data: network } = useNetwork()

  const { authorized } = hasActionPermission(
    network.authMemberProps?.permissions ?? [],
    'exportMemberRequest',
  )

  const {
    data: exportRequest,
    isError,
    isLoading,
  } = useExportMemberRequest({
    variables: {
      id,
    },
    fields: 'all',
    useQueryOptions: {
      retry: false,
      enabled: authorized,
    },
  })

  const valid =
    authorized &&
    !isError &&
    !isLoading &&
    exportRequest?.status === ExportStatus.Done

  useEffect(() => {
    if (!isLoggedIn) {
      showLogin()
    }
  }, [isLoggedIn, showLogin])

  return (
    <div className="lg:w-1/2 md:w-full flex mx-auto flex-col py-8 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-12 w-auto"
          src={(network?.logo as Image)?.url}
          alt="Workflow"
        />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-4 px-4 shadow sm:rounded-lg sm:px-10">
          <h2 className="text-center text-lg font-bold text-content">
            {valid && 'Your member data is ready for downloading'}
            {!valid && !isLoading && 'Your link is invalid or expired'}
            {isLoading && (
              <div className="h-4 m-6 bg-skeleton rounded-full animate-pulse" />
            )}
          </h2>
          <div className="mt-6 text-center">
            {valid && (
              <Button
                variant="secondaryNeutral"
                as="button"
                className="me-2"
                loading={isLoading}
              >
                <a
                  href={exportRequest?.file?.downloadUrl}
                  download={exportRequest?.file?.name}
                  onClick={() => {
                    analytics.track('Download Export Member', {
                      id: exportRequest?.id,
                    })
                  }}
                >
                  <T defaultMessage="Download" id="Generics.Download" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
